// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import * as api from '../../api/client'
import ShippingLabel, { type LabelData } from './ShippingLabel'
import LabelErrorBoundary from './LabelErrorBoundary'

/**
 * Print view for a single shipment's label (Sprint 18). Fetches the label
 * config + the shipment's label data, renders the size-aware label, and opens
 * the browser print dialog. Reached at /shipments/:id/label (gated
 * shipping.create — same as creating/cancelling, since it exposes the address).
 */
export default function LabelPrintView() {
  const { id } = useParams<{ id: string }>()
  const [config, setConfig] = useState<api.LabelConfig | null>(null)
  const [data, setData] = useState<LabelData | null>(null)
  const [error, setError] = useState<string | null>(null)
  // ASP-90 (Patrick): NEVER print a label without a courier order number. A
  // pending_confirmation shipment renders a wait screen with a Refresh action.
  const [pending, setPending] = useState(false)
  const [refreshBusy, setRefreshBusy] = useState(false)
  const [reloadTick, setReloadTick] = useState(0)
  const refreshOrder = async () => {
    if (!id || refreshBusy) return
    setRefreshBusy(true)
    try {
      const r = await api.confirmShipmentOrder(id)
      if (r.confirmed) { setPending(false); setReloadTick(t => t + 1) }
    } catch { /* stay pending */ } finally { setRefreshBusy(false) }
  }

  useEffect(() => {
    if (!id) return
    Promise.all([api.getLabelConfig(), api.getShipmentLabelData(id)])
      .then(([cfg, d]) => {
        if (d.status === 'pending_confirmation') { setPending(true); setConfig(cfg.config); setData(null); return }
        setPending(false)
        setConfig(cfg.config)
        setData({
          carrier: d.carrier,
          trackingNumber: d.tracking_number,
          orderRef: d.order_ref,
          pharmacyName: d.pharmacy_name,
          pharmacyLogoUrl: cfg.config.logo_mode === 'custom' ? cfg.config.custom_logo_url
            : cfg.config.logo_mode === 'pharmacy' ? d.pharmacy_logo_url : null,
          fromLine: d.from_line,
          recipientName: d.recipient_name,
          toLine1: d.to_line1,
          toCityStateZip: d.to_city_state_zip,
          toPhone: d.to_phone,
          coldChain: d.cold_chain,
          signature: d.require_signature,
          adultSignature: d.adult_signature,
          saturday: d.saturday,
          instructions: d.instructions,
          rxNumber: d.rx_number,
          rxNumbers: d.rx_numbers ?? null,
          serviceLevel: d.service_level ?? null,
          shipDate: d.ship_date ?? null,
          isManual: d.is_manual,
          copay: d.copay,
          copayItems: d.copay_items ?? null,
          copayCollected: d.copay_collected ?? false,
          copayPaidAmount: d.copay_paid_amount ?? null,
          copayState: d.copay_state,
          copayCollectTotal: d.copay_collect_total,
          copayUnitemised: d.copay_unitemised,
          facilityType: d.facility_type ?? null,
          facilityTypeLabel: d.facility_type_label ?? null,
          facilityName: d.facility_name ?? null,
          facilityPhone: d.facility_phone ?? null,
          shipmentKind: d.shipment_kind ?? null,
          rxLines: d.rx_lines?.map(l => ({ rxNumber: l.rx_number, patientName: l.patient_name, drugName: l.drug_name })) ?? null,
        })
      })
      .catch((e: any) => setError(e?.message || 'Could not load label.'))
  }, [id, reloadTick])

  // Auto-open print once rendered. Focus the tab first — a tab opened via
  // window.open() from the create flow can be unfocused/backgrounded, and browsers
  // defer or drop window.print() on an unfocused tab (this caused "opened but didn't
  // print"). Focusing, then printing on the next frame after paint, makes it fire.
  // AUTOPRINT FIX (2026-08-04): the old sequence was setTimeout(500) → focus() →
  // requestAnimationFrame(print). rAF callbacks DO NOT RUN in a hidden tab, so when
  // this tab opened in the background (the common case when the create flow opens it
  // after an awaited courier call) print() was simply never reached — the pharmacist
  // saw a tab appear and had to press Print. focus() is also a no-op without user
  // activation, so it could not rescue it.
  //
  // Now: wait for the things that actually matter (fonts + the pharmacy logo, so the
  // label isn't printed half-rendered), print DIRECTLY (no rAF), and if the tab is
  // hidden, wait for it to become visible and print then. `printedRef` guarantees we
  // only ever fire once, and drives the autoclose timer below so a tab that never
  // printed is never auto-closed.
  const [printed, setPrinted] = useState(false)
  const printedRef = useRef(false)
  useEffect(() => {
    if (!config || !data || pending || printedRef.current) return
    let cancelled = false
    const fire = () => {
      if (cancelled || printedRef.current) return
      if (document.visibilityState === 'hidden') return   // retried on visibilitychange
      printedRef.current = true
      setPrinted(true)
      try { window.focus() } catch { /* ignore */ }
      try { window.print() } catch { /* ignore — the manual Print button still works */ }
    }
    const onVisible = () => { if (document.visibilityState === 'visible') fire() }
    document.addEventListener('visibilitychange', onVisible)
    // Assets settled → print. Images that are already cached resolve immediately.
    const imgs = Array.from(document.images)
    const waitImgs = Promise.all(imgs.map(i => i.complete ? Promise.resolve()
      : new Promise<void>(res => { i.addEventListener('load', () => res(), { once: true }); i.addEventListener('error', () => res(), { once: true }) })))
    const waitFonts = (document as any).fonts?.ready ?? Promise.resolve()
    // Cap the wait so a stalled asset can never block printing entirely.
    const timeoutGuard = new Promise<void>(res => setTimeout(res, 2500))
    Promise.race([Promise.all([waitImgs, waitFonts]).then(() => undefined), timeoutGuard]).then(fire)
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVisible) }
  }, [config, data, pending])

  // D-SHIP-16 (Patrick): when the create flow auto-opens this tab (?autoclose=1),
  // close it once printing is done so dispatch never accumulates tabs. Works in
  // both modes: under Chrome --kiosk-printing the dialog is skipped and afterprint
  // fires immediately; in normal mode afterprint fires when the user finishes the
  // dialog. A fallback timer covers browsers that don't emit afterprint. Only the
  // auto-opened tab closes — the manually opened label (list printer icon) never
  // self-closes because it isn't given ?autoclose.
  useEffect(() => {
    const autoclose = new URLSearchParams(window.location.search).get('autoclose') === '1'
    // Gated on `printed` (2026-08-04): the fallback timer used to run from render, so a
    // tab that never managed to print (backgrounded, blocked) was closed anyway after
    // 20s — losing the label AND the evidence. Now the countdown only starts once
    // print() has actually been invoked.
    if (!autoclose || !config || !data || pending || !printed) return
    let closed = false
    const done = () => { if (closed) return; closed = true; try { window.close() } catch { /* ignore */ } }
    window.addEventListener('afterprint', done)
    // Fallback only for kiosk configs that never emit afterprint. Kept long so it
    // never closes the tab while a normal print dialog is still open.
    const t = setTimeout(done, 20000)
    return () => { window.removeEventListener('afterprint', done); clearTimeout(t) }
  }, [config, data, pending, printed])

  if (error) return <div style={{ padding: 24, color: '#dc2626' }}>{error}</div>
  if (pending) return (
    <div style={{ padding: 32, maxWidth: 460, margin: '0 auto', textAlign: 'center' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#92400e' }}>⚠ Waiting for the courier order number</div>
      <div style={{ fontSize: 13, marginTop: 8, color: '#78350f' }}>
        The order was sent to the courier, but no order number has been assigned yet.
        Labels can't be printed until the order is confirmed.
      </div>
      <button type="button" onClick={() => void refreshOrder()} disabled={refreshBusy}
        style={{ marginTop: 14, border: '1px solid #d97706', background: '#d97706', color: '#fff', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
        {refreshBusy ? 'Checking…' : '↻ Refresh — check for order number'}
      </button>
    </div>
  )
  if (!config || !data) return <div style={{ padding: 24 }}>Preparing label…</div>

  // Size the printed page to the configured label stock so a label printer (or
  // Print-to-PDF) emits one correctly-sized label rather than a Letter sheet with
  // the label in the corner. Chrome honors @page size for both. The printer driver
  // should also be set to the same stock; this aligns the browser's page box to it.
  // Thermal label drivers (KNAON Y41BT etc.) report 4x6 stock as 100mm x 150mm,
  // NOT 4in x 6in (=101.6 x 152.4mm). Sending inches makes the browser scale the
  // page down to the driver's slightly-smaller metric paper, leaving whitespace.
  // Match the driver's metric size exactly so the label fills the whole stock.
  const PAGE_SIZE: Record<string, string> = { '4x6': '100mm 150mm', '4x3': '100mm 76mm', 'letter': '8.5in 11in' }
  const pageSize = PAGE_SIZE[config.size] ?? '4in 6in'

  return (
    <div className="label-print-wrap" style={{ padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 16 }}>
      <style>{`
        /* @page MUST be top-level. Chrome ignores @page nested inside @media print and
           drops BOTH its size and margin descriptors — which is why the printed page kept
           leaking the user's default paper (Letter) plus headers/footers. @page only applies
           to paged (print) media anyway, so top-level is correct. Honored by Chrome's
           "Save as PDF" and label printers set to this stock; a system printer's own paper
           size still wins for that destination (set the printer to the label stock there). */
        @page { size: ${pageSize}; margin: 0; }
        @media print {
          html, body {
            margin: 0 !important; padding: 0 !important;
            width: ${pageSize.split(' ')[0]} !important; height: ${pageSize.split(' ')[1]} !important;
            overflow: hidden !important;
          }
          .no-print { display: none !important; }
          /* The label IS the page on print: zero the wrapper chrome and pin the
             label to the top-left so it fills the label stock exactly. */
          .label-print-wrap {
            padding: 0 !important; margin: 0 !important; gap: 0 !important;
            display: block !important;
          }
        }
      `}</style>
      <div className="no-print" style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => window.print()} style={{ background: '#0369a1', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}>Print label</button>
        <button onClick={() => window.close()} style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}>Close</button>
      </div>
      {/* There is no error boundary anywhere else in the app, so a label render
          failure would unmount the root and leave a BLANK print tab — the operator
          loses both the label and any clue why. Fall back to the shipment's
          identifying details so the package can still be handled by hand. */}
      <LabelErrorBoundary
        resetKey={data}
        fallbackLines={[
          data.recipientName, data.toLine1, data.toCityStateZip,
          data.orderRef && `Order ${data.orderRef}`,
          data.trackingNumber && `Tracking ${data.trackingNumber}`,
        ]}>
        <ShippingLabel config={config} data={data} />
      </LabelErrorBoundary>
    </div>
  )
}
